import { useMutation, useQuery, useQueryClient } from 'react-query'
import {
  useAPI,
  useChuckClientContext,
  useSocketMessageListener
} from '@gopeerproject/chuck'
import { MemberControls as MC } from '@constants'
import { useClassroomEnv } from '../../../ClassroomEnv'
import { useCallback } from 'react'

const queryKey = 'member_controls'

// type MsgPayload = {
//   appointmentId: ID
//   status?: WaitingRoomStatus
//   video?: MediaRestrictionStatus
//   audio?: MediaRestrictionStatus
//   participants: {
//     id: ID
//     status?: WaitingRoomStatus
//     video?: MediaRestrictionStatus
//     audio?: MediaRestrictionStatus
//   }[]
// }
// Updates the result of [member_controls, id] query in a way that it only updates the necessary fields
// Back-end only sends the fields that have changed, so we need to merge them with the previous data
const updateMemberControlsData =
  (data = {}) =>
  (prevData) => {
    const { status, video, audio, participants } = data
    const prevParticipantIds = new Set(prevData?.participants?.map((p) => p.id))
    const newParticipantIds = new Set(participants.map((p) => p.id))
    const allParticipantIds = Array.from(
      new Set([...prevParticipantIds, ...newParticipantIds])
    )

    const newParticipants = allParticipantIds.map((id) => {
      const prevParticipant = prevData?.participants?.find((p) => p.id === id)
      const newParticipant = participants.find((p) => p.id === id)

      return {
        id,
        ...prevParticipant,
        ...newParticipant
      }
    })

    return {
      ...prevData,
      ...(status ? { status } : {}),
      ...(video ? { video } : {}),
      ...(audio ? { audio } : {}),
      participants: newParticipants
    }
  }

export const useMemberControls = (options = {}) => {
  const { get } = useAPI()
  const { client } = useChuckClientContext()
  const { id } = useClassroomEnv()

  return useQuery(
    [queryKey, id],
    () => get({ url: `/classroom_groups/${id}` }).then((res) => res.data),
    {
      onError: (err) => {
        client.captureException(err)
      },
      refetchOnWindowFocus: false,
      refetchOnmount: false,
      refetchOnReconnect: false,
      retry: false,
      staleTime: Infinity,
      ...options
    }
  )
}

export const useMemberAdmissionAction = () => {
  const { post } = useAPI()
  const { client } = useChuckClientContext()
  const { id } = useClassroomEnv()
  const queryClient = useQueryClient()

  return useMutation(
    ({ participantId, status }) =>
      post({
        url: `/classroom_groups/${id}/manage_admission`,
        data: { participantId, status }
      }).then((res) => res.data),
    {
      onMutate: ({ participantId, status }) => {
        // Optimistically mark member as admitted
        queryClient.setQueriesData(
          [queryKey, id],
          updateMemberControlsData({
            participants: [{ id: participantId, status }]
          })
        )
      },
      onSuccess: (data) => {
        // Update member data with actual data on success
        queryClient.setQueryData([queryKey, id], updateMemberControlsData(data))
      },
      onError: (err, { participantId }) => {
        // Rollback on error
        queryClient.setQueryData(
          [queryKey, id],
          updateMemberControlsData({
            participants: [
              { id: participantId, status: MC.WaitingRoomStatus.WAITING }
            ]
          })
        )

        client.captureException(err)
      }
    }
  )
}

export const useMemberAdmitAllAction = () => {
  const { post } = useAPI()
  const { client } = useChuckClientContext()
  const { id } = useClassroomEnv()
  const queryClient = useQueryClient()

  return useMutation(
    () =>
      post({
        url: `/classroom_groups/${id}/admit_all`
      }).then((res) => res.data),
    {
      onMutate: () => {
        // Optimistically mark all members as admitted
        const prevWaitingParticipants = queryClient
          .getQueryData([queryKey, id])
          .participants.filter((p) => p.status === MC.WaitingRoomStatus.WAITING)

        const optimisticParticipants = prevWaitingParticipants.map((p) => ({
          ...p,
          status: MC.WaitingRoomStatus.ADMITTED
        }))

        queryClient.setQueryData(
          [queryKey, id],
          updateMemberControlsData({
            participants: optimisticParticipants
          })
        )

        return { prevWaitingParticipants }
      },
      onSuccess: (data) => {
        // Update member data with actual data on success
        queryClient.setQueryData([queryKey, id], updateMemberControlsData(data))
      },
      onError: (err, vars, context) => {
        // Rollback on error
        queryClient.setQueryData(
          [queryKey, id],
          updateMemberControlsData({
            participants: context.prevWaitingParticipants
          })
        )

        client.captureException(err)
      }
    }
  )
}

export const useMemberMediaRestrictionAction = () => {
  const { post } = useAPI()
  const { client } = useChuckClientContext()
  const { id } = useClassroomEnv()
  const queryClient = useQueryClient()

  return useMutation(
    ({ participantId, mediaType, status }) => {
      return post({
        url: `/classroom_groups/${id}/manage_media_restrictions`,
        data: {
          participantId,
          mediaType,
          status: status
        }
      }).then((res) => res.data)
    },
    {
      onMutate: ({ participantId, mediaType }) => {
        // Optimistically update member media restrictions

        const currentParticipant = queryClient
          .getQueryData([queryKey, id])
          .participants.find((p) => p.id === participantId)

        queryClient.setQueryData(
          [queryKey, id],
          updateMemberControlsData({
            participants: [
              {
                id: participantId,
                [mediaType]: reverseMediaRestrictionStatus(
                  currentParticipant[mediaType]
                )
              }
            ]
          })
        )

        return { [mediaType]: currentParticipant[mediaType] }
      },
      onSuccess: (data) => {
        // Update member data with actual data on success
        queryClient.setQueryData([queryKey, id], updateMemberControlsData(data))
      },
      onError: (err, { participantId, mediaType }, context) => {
        // Rollback on error
        queryClient.setQueryData(
          [queryKey, id],
          updateMemberControlsData({
            participants: [
              { id: participantId, [mediaType]: context[mediaType] }
            ]
          })
        )

        client.captureException(err)
      }
    }
  )
}

export const useMemberRestrictAudioAllAction = () => {
  const { post } = useAPI()
  const { client } = useChuckClientContext()
  const { id } = useClassroomEnv()
  const queryClient = useQueryClient()

  return useMutation(
    () =>
      post({
        url: `/classroom_groups/${id}/restrict_audio_all`
      }).then((res) => res.data),
    {
      onMutate: () => {
        // Optimistically mute all members
        const prevUnrestrictedAudioParticipants = queryClient
          .getQueryData([queryKey, id])
          .participants.filter(
            (p) => p.audio !== MC.MediaRestrictionStatus.RESTRICTED
          )

        const optimisticParticipants = prevUnrestrictedAudioParticipants.map(
          (p) => ({
            ...p,
            audio: MC.MediaRestrictionStatus.RESTRICTED
          })
        )

        queryClient.setQueryData(
          [queryKey, id],
          updateMemberControlsData({
            participants: optimisticParticipants
          })
        )

        return { prevUnrestrictedAudioParticipants }
      },
      onSuccess: (data) => {
        // Update member data with actual data on success
        queryClient.setQueryData([queryKey, id], updateMemberControlsData(data))
      },
      onError: (err, vars, context) => {
        // Rollback on error
        queryClient.setQueryData(
          [queryKey, id],
          updateMemberControlsData({
            participants: context.prevUnrestrictedAudioParticipants
          })
        )

        client.captureException(err)
      }
    }
  )
}

export const useMemberControlsRealtimeActionsHandler = () => {
  const queryClient = useQueryClient()

  const handler = useCallback(
    (payload) => {
      queryClient.setQueryData(
        [queryKey, payload.appointmentId],
        updateMemberControlsData(payload)
      )
    },
    [queryClient]
  )
  useSocketMessageListener('update:classroom_group', handler)
}

export function reverseMediaRestrictionStatus(status) {
  if (status === MC.MediaRestrictionStatus.RESTRICTED)
    return MC.MediaRestrictionStatus.UNRESTRICTED
  return MC.MediaRestrictionStatus.RESTRICTED
}

// video/audio: true - means unrestricted
export function mediaRestrictionStatusToBoolean(status) {
  if (status === MC.MediaRestrictionStatus.RESTRICTED) return false
  return true
}
